import React from "react";
import { IonModal, IonIcon, IonButton } from "@ionic/react";
import { close } from "ionicons/icons";
import {
  POPUP_ACTIONS_STYLE,
  POPUP_BODY_STYLE,
  POPUP_SHEET_STYLE,
} from "../../../components/ui/popups/ActionButtons";

// F-506 (UAT, URS_5.5): the Data Sharing screen has ONE "Share my data" button. Pressing it opens
// this chooser, and each choice continues into the share flow that already existed for it:
// "people" = the person-invite form, "org" = the organization search (both in SharingModal).
// The bottom-sheet shell matches `ActionButtons` (the screen's other popups) and shares its layout
// styles: header, a scrolling text area, and the buttons in normal flow below it.
//
// F-525 (UAT, URS_6.8): "Update the buttons to be individual or organization. The buttons are
// covering up the text in the pop-up." The old labels ("A person (patient or caregiver)", "An
// organization (HME/location)") were wider than the 300px button at the theme's forced 20px, so
// they wrapped inside a fixed 46px-high button and spilled over the question above. The labels are
// now exactly "Individual" and "Organization", and the layout keeps buttons off the text anyway.
export type ShareTarget = "people" | "org";

export const SHARE_CHOOSER_TITLE = "Share my data";
export const SHARE_CHOOSER_QUESTION = "Who would you like to share your data with?";
export const SHARE_CHOOSER_PERSON = "Individual";
export const SHARE_CHOOSER_ORG = "Organization";
// F-507: moved here from the removed "Did you know?" card.
export const SHARE_CHOOSER_ORG_BLOCKED =
  "Sharing with organizations isn't available on your account.";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onChoose: (target: ShareTarget) => void;
  /** Org sharing is blocked for this account: the organization option shows disabled, with why. */
  orgBlocked: boolean;
};

const buttonStyle = {
  "--border-radius": "30px",
  fontSize: "17px",
  fontWeight: "500",
} as React.CSSProperties;

const ShareDataChooser: React.FC<Props> = ({ isOpen, onClose, onChoose, orgBlocked }) => (
  <IonModal
    isOpen={isOpen}
    onDidDismiss={onClose}
    presentingElement={document.querySelector("ion-app") || undefined}
    canDismiss={true}
    showBackdrop={true}
    backdropDismiss={true}
    style={{
      "--background": "transparent",
      "--box-shadow": "none",
      "--border-radius": "20px",
    }}
  >
    <div
      data-testid="share-data-chooser"
      style={POPUP_SHEET_STYLE}
    >
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "16px",
        }}
      >
        <div style={{ width: "40px" }}></div>
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "700",
            margin: "0",
            color: "#000",
            flex: 1,
            textAlign: "center",
          }}
        >
          {SHARE_CHOOSER_TITLE}
        </h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#999",
            cursor: "pointer",
            padding: "8px",
            width: "40px",
            height: "40px",
            display: "flex",
          }}
        >
          <IonIcon icon={close} style={{ fontSize: "24px" }} />
        </button>
      </div>
      <div data-testid="share-data-chooser-body" style={POPUP_BODY_STYLE}>
        <p className="text-center px-4 mb-2" style={{ fontSize: "16px", color: "#586670" }}>
          {SHARE_CHOOSER_QUESTION}
        </p>
      </div>
      <div data-testid="share-data-chooser-actions" style={POPUP_ACTIONS_STYLE}>
        <IonButton
          className="primary-button !w-[275px]"
          fill="solid"
          style={{ ...buttonStyle, marginBottom: "16px" }}
          onClick={() => onChoose("people")}
        >
          {SHARE_CHOOSER_PERSON}
        </IonButton>
        <IonButton
          className="tertiary-button !w-[275px]"
          fill="outline"
          style={{ ...buttonStyle, borderRadius: "30px" }}
          disabled={orgBlocked}
          onClick={() => {
            if (!orgBlocked) onChoose("org");
          }}
        >
          {SHARE_CHOOSER_ORG}
        </IonButton>
        {orgBlocked && (
          <p className="text-center px-4 mt-2" style={{ fontSize: "14px", color: "#586670" }}>
            {SHARE_CHOOSER_ORG_BLOCKED}
          </p>
        )}
      </div>
    </div>
  </IonModal>
);

export default ShareDataChooser;
